/**
 * Espace courant (lot 1d, 2026-10-28) : « mes espaces » (route acteur), espace sélectionné
 * (localStorage, envoyé en `X-Workspace` par lib/api sur chaque requête), rôle dans cet espace,
 * changement d'espace (rechargement complet : toutes les données changent de propriétaire).
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest, getWorkspaceId, setWorkspaceId } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export type WorkspaceRole = 'owner' | 'admin' | 'editeur' | 'conseiller';
export interface Membership { id: string; owner_user_id: string; owner_email: string; owner_name: string | null; role: Exclude<WorkspaceRole, 'owner'>; accepted_at: string | null }

export function useMemberships(enabled: boolean) {
  return useQuery({
    queryKey: ['workspace', 'memberships'],
    queryFn: () => apiRequest<{ memberships: Membership[] }>('/workspace/memberships'),
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useWorkspace() {
  const { user } = useAuth();
  const memberships = useMemberships(!!user);
  const list = memberships.data?.memberships ?? [];
  const stored = getWorkspaceId();
  const foreign = !!stored && !!user && stored !== user.id;
  const current = foreign ? list.find((m) => m.owner_user_id === stored) ?? null : null;
  // Tant que la liste n'est pas là, on suppose « éditeur » (le serveur reste juge, l'interface ne fait que masquer).
  const role: WorkspaceRole = foreign ? current?.role ?? 'editeur' : 'owner';
  const select = (id: string | null) => {
    setWorkspaceId(id && user && id !== user.id ? id : null);
    window.location.assign('/');
  };
  return { user, memberships: list, ready: memberships.isSuccess, current, currentId: foreign ? stored : null, role, select };
}

const AUTO_KEY = 'nia-workspace-auto';

/**
 * Au premier passage d'un compte CRM M&R membre d'un seul espace d'équipe, cet espace devient
 * l'espace courant (13.5) ; un espace devenu inaccessible (retrait) est oublié.
 */
export function useWorkspaceBootstrap(): void {
  const { user, memberships, ready, currentId } = useWorkspace();
  useEffect(() => {
    if (!user || !ready) return;
    if (currentId && !memberships.some((m) => m.owner_user_id === currentId)) {
      setWorkspaceId(null);
      window.location.assign('/');
      return;
    }
    let done = false;
    try { done = sessionStorage.getItem(AUTO_KEY) === '1'; } catch { /* stockage indisponible */ }
    if (!currentId && !done && user.origin === 'mr_crm' && memberships.length === 1) {
      try { sessionStorage.setItem(AUTO_KEY, '1'); } catch { /* ignore */ }
      setWorkspaceId(memberships[0]!.owner_user_id);
      window.location.assign('/');
    }
  }, [user, ready, currentId, memberships]);
}
